import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCurrency } from "@/lib/currencies";
import { CurrencySelect } from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  CalendarIcon,
  RefreshCw,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialExpense = {
  title: "",
  amount: "",
  currency: "EUR",
  category_id: "",
  description: "",
  expense_type: "fix",
  vendor: "",
  is_recurring: true,
  recurring_interval: "monthly",
  recurring_day: 1,
  contract_start: "",
  contract_end: "",
  cancellation_notice_days: 30,
};

export default function RecurringPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState(initialExpense);
  const [saving, setSaving] = useState(false);
  const [contractStartDate, setContractStartDate] = useState(null);
  const [contractEndDate, setContractEndDate] = useState(null);
  const { t } = useLanguage();

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/expenses?expense_type=fix`),
        axios.get(`${API}/categories`),
      ]);
      // Filter only recurring expenses
      const recurringExpenses = expensesRes.data.filter(e => e.is_recurring);
      setExpenses(recurringExpenses);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        title: expense.title,
        amount: expense.amount.toString(),
        currency: expense.currency || "EUR",
        category_id: expense.category_id,
        description: expense.description || "",
        expense_type: expense.expense_type,
        vendor: expense.vendor || "",
        is_recurring: expense.is_recurring,
        recurring_interval: expense.recurring_interval || "monthly",
        recurring_day: expense.recurring_day || 1,
        contract_start: expense.contract_start || "",
        contract_end: expense.contract_end || "",
        cancellation_notice_days: expense.cancellation_notice_days || 30,
      });
      setContractStartDate(expense.contract_start ? parseISO(expense.contract_start) : null);
      setContractEndDate(expense.contract_end ? parseISO(expense.contract_end) : null);
    } else {
      setEditingExpense(null);
      setFormData(initialExpense);
      setContractStartDate(null);
      setContractEndDate(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setFormData(initialExpense);
    setContractStartDate(null);
    setContractEndDate(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: contractStartDate ? format(contractStartDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        contract_start: contractStartDate ? format(contractStartDate, "yyyy-MM-dd") : null,
        contract_end: contractEndDate ? format(contractEndDate, "yyyy-MM-dd") : null,
        recurring_day: parseInt(formData.recurring_day),
        cancellation_notice_days: parseInt(formData.cancellation_notice_days) || null,
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success("Vertrag aktualisiert");
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success("Vertrag erstellt");
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Vertrag wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success("Vertrag gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const getIntervalLabel = (interval) => {
    switch (interval) {
      case "monthly": return t("recurring.monthly");
      case "yearly": return t("recurring.yearly");
      case "quarterly": return t("recurring.quarterly");
      default: return interval;
    }
  };

  // Calculate monthly cost
  const totalMonthlyCost = expenses.reduce((sum, exp) => {
    let monthly = exp.amount;
    if (exp.recurring_interval === "yearly") monthly = exp.amount / 12;
    if (exp.recurring_interval === "quarterly") monthly = exp.amount / 3;
    return sum + monthly;
  }, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("recurring.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("recurring.subtitle")}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#E31E24] hover:bg-[#c91a1f]"
              onClick={() => handleOpenDialog()}
              data-testid="add-recurring-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("recurring.newContract")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">
                {editingExpense ? t("recurring.editContract") : t("recurring.newContract")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Bezeichnung *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Microsoft 365 Business"
                    required
                    data-testid="recurring-title"
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Betrag *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      className="flex-1"
                      data-testid="recurring-amount"
                    />
                    <CurrencySelect
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="interval">Intervall *</Label>
                  <Select
                    value={formData.recurring_interval}
                    onValueChange={(value) => setFormData({ ...formData, recurring_interval: value })}
                  >
                    <SelectTrigger data-testid="recurring-interval">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monatlich</SelectItem>
                      <SelectItem value="quarterly">Quartalsweise</SelectItem>
                      <SelectItem value="yearly">Jährlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="day">Zahlungstag</Label>
                  <Input
                    id="day"
                    type="number"
                    min="1"
                    max="28"
                    value={formData.recurring_day}
                    onChange={(e) => setFormData({ ...formData, recurring_day: e.target.value })}
                    data-testid="recurring-day"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Kategorie *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="recurring-category">
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="vendor">Anbieter</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="z.B. Microsoft GmbH"
                    data-testid="recurring-vendor"
                  />
                </div>
                <div>
                  <Label>Vertragsbeginn</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="recurring-start"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {contractStartDate 
                          ? format(contractStartDate, "dd.MM.yyyy", { locale: de })
                          : "Auswählen..."
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={contractStartDate}
                        onSelect={setContractStartDate}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Vertragsende</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="recurring-end"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {contractEndDate 
                          ? format(contractEndDate, "dd.MM.yyyy", { locale: de })
                          : "Auswählen..."
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={contractEndDate}
                        onSelect={setContractEndDate}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notice">Kündigungsfrist (Tage)</Label>
                  <Input
                    id="notice"
                    type="number"
                    min="0"
                    value={formData.cancellation_notice_days}
                    onChange={(e) => setFormData({ ...formData, cancellation_notice_days: e.target.value })}
                    placeholder="z.B. 30"
                    data-testid="recurring-notice"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Sie werden erinnert, bevor die Kündigungsfrist abläuft
                  </p>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="description">Notizen</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Zusätzliche Informationen zum Vertrag..."
                    rows={2}
                    data-testid="recurring-description"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Abbrechen
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#E31E24] hover:bg-[#c91a1f]"
                  disabled={saving}
                  data-testid="save-recurring-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    "Speichern"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Card className="card-hover col-span-2 md:col-span-1" data-testid="stat-contracts">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("recurring.activeContracts")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">{expenses.length}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 md:h-6 md:w-6 text-[#E31E24]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-monthly-cost">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("recurring.monthlyCosts")}</p>
                <p className="text-lg md:text-3xl font-bold mt-1 md:mt-2 font-[JetBrains_Mono] truncate">
                  {formatCurrency(totalMonthlyCost)}
                </p>
              </div>
              <div className="hidden sm:flex h-10 w-10 md:h-12 md:w-12 rounded-full bg-[#09090b]/10 items-center justify-center flex-shrink-0 ml-2">
                <CalendarIcon className="h-5 w-5 md:h-6 md:w-6 text-[#09090b]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-yearly-cost">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("recurring.yearlyCosts")}</p>
                <p className="text-lg md:text-3xl font-bold mt-1 md:mt-2 font-[JetBrains_Mono] truncate">
                  {formatCurrency(totalMonthlyCost * 12)}
                </p>
              </div>
              <div className="hidden sm:flex h-10 w-10 md:h-12 md:w-12 rounded-full bg-amber-500/10 items-center justify-center flex-shrink-0 ml-2">
                <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {expenses.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine wiederkehrenden Ausgaben vorhanden.</p>
              <p className="text-sm">Fügen Sie Ihre Verträge und Abos hinzu.</p>
            </CardContent>
          </Card>
        ) : (
          expenses.map((expense) => (
            <Card key={expense.id} className="card-hover" data-testid={`recurring-card-${expense.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{expense.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {expense.vendor || "—"}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div 
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: expense.category?.color || "#71717a" }}
                      />
                      <span className="text-xs text-muted-foreground">{expense.category?.name || "—"}</span>
                      <Badge variant="secondary" className="text-xs">
                        {getIntervalLabel(expense.recurring_interval)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Ende: {expense.contract_end 
                        ? format(parseISO(expense.contract_end), "dd.MM.yyyy")
                        : t("recurring.unlimited")
                      }
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-bold font-[JetBrains_Mono]">
                      {formatCurrency(expense.amount, expense.currency)}
                    </p>
                    <div className="flex items-center justify-end gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenDialog(expense)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(expense.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Anbieter</TableHead>
                <TableHead className="text-center">Intervall</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Vertragsende</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine wiederkehrenden Ausgaben vorhanden.</p>
                    <p className="text-sm">Fügen Sie Ihre Verträge und Abos hinzu.</p>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => (
                  <TableRow key={expense.id} className="table-row-hover" data-testid={`recurring-row-${expense.id}`}>
                    <TableCell className="font-medium">{expense.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: expense.category?.color || "#71717a" }}
                        />
                        {expense.category?.name || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {expense.vendor || "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {getIntervalLabel(expense.recurring_interval)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-[JetBrains_Mono] font-medium">
                      {formatCurrency(expense.amount, expense.currency)}
                    </TableCell>
                    <TableCell>
                      {expense.contract_end 
                        ? format(parseISO(expense.contract_end), "dd.MM.yyyy")
                        : t("recurring.unlimited")
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(expense)}
                          data-testid={`edit-recurring-${expense.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(expense.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-recurring-${expense.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
